/**
 * Pure transition table for the anchor stage's (`AnchorStage.tsx`) video placeholder -> player
 * swap: scroll-into-view and a click both drive the SAME transition out of `"not-loaded"` —
 * click is an explicit fallback for "hasn't loaded yet", not a second, different loading path.
 * Once loading has started, a redundant scroll or click is a no-op (same state back out) — this is
 * what makes the click handler safe to leave wired even after the `IntersectionObserver` has
 * already fired, with no double-load/restart.
 *
 * Extracted as a standalone pure function (this repo's convention for render/state decisions,
 * `checkOrder.ts`/`ControlRoomScreen.tsx`'s `pageBodyState` etc.) specifically so the state machine
 * is unit-tested directly, without needing React Testing Library (not part of this repo's stack).
 */
export type VideoLoadState = "not-loaded" | "loading" | "loaded";

export type VideoLoadEvent = "scroll" | "click" | "proxy-ready";

export function nextVideoLoadState(
  state: VideoLoadState,
  event: VideoLoadEvent
): VideoLoadState {
  if (event === "proxy-ready") {
    // Only a real transition once loading is actually underway — a stray "ready" signal before
    // anything was requested (shouldn't happen, but this keeps the table total) leaves the
    // placeholder up rather than jumping straight to "loaded" with nothing requested.
    return state === "loading" ? "loaded" : state;
  }
  // scroll or click: only "not-loaded" ever moves — already loading/loaded is a no-op.
  return state === "not-loaded" ? "loading" : state;
}
